import React, { useState, useEffect, useCallback } from 'react';
import apiClient, { endpoints } from '../api/client';
import { FiPlus, FiDownload, FiTrash2, FiCalendar, FiUser, FiAward, FiAlertTriangle, FiCheckCircle, FiSearch, FiFilter, FiEdit, FiChevronLeft, FiChevronRight, FiBell } from 'react-icons/fi';
import CreateCertificateModal from '../components/CreateCertificateModal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import EditCertificateModal from '../components/EditCertificateModal';

const Certificates = () => {
  const { isLimitedAdmin } = useAuth();
  const { showError } = useToast();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    certificate_type: '',
    status: '',
    expiry_days: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCertificates: 0,
    hasNext: false,
    hasPrev: false,
    limit: 10
  });

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filters.certificate_type) params.append('type', filters.certificate_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.expiry_days) params.append('expiring_days', filters.expiry_days);
      
      // Handle expiry filter
      // if (filters.expiry_days) {
      //   const days = parseInt(filters.expiry_days);
      //   const endDate = new Date();
      //   endDate.setDate(endDate.getDate() + days);
      //   params.append('expiry_end', endDate.toISOString().split('T')[0]);
      // }

      const response = await apiClient.get(`${endpoints.certificates.list}?${params}`);
      
      if (response.data.success) {
        // Fix: Correct the data path - backend returns data as direct array, not nested in certificates
        const certificatesData = response.data.data || [];
        const paginationData = response.data.pagination || {
          current_page: 1,
          per_page: pagination.limit,
          total: 0,
          total_pages: 1
        };
        
        setCertificates(Array.isArray(certificatesData) ? certificatesData : []);
        setPagination(prev => ({
          ...prev,
          currentPage: paginationData.current_page,
          totalPages: paginationData.total_pages,
          totalCertificates: paginationData.total,
          hasNext: paginationData.current_page < paginationData.total_pages,
          hasPrev: paginationData.current_page > 1,
          limit: paginationData.per_page
        }));
      } else {
        // Handle API error response
        setCertificates([]);
        setError(response.data.message || 'Failed to load certificates');
        showError(response.data.message || 'Failed to load certificates');
      }
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setCertificates([]);
      setError('Failed to load certificates');
      showError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }
  
  useEffect(() => {
    fetchCertificates();
  }, [searchTerm, filters, pagination.currentPage, pagination.limit]);

  const handleEditCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setShowEditModal(true);
  };

  const handleCertificateCreated = (newCertificate) => {
    fetchCertificates(); // Refresh the list
    setShowCreateModal(false);
  };

  const handleCertificateUpdated = (updatedCertificate) => {
    fetchCertificates(); // Refresh the list
    setShowEditModal(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      expiring: 'bg-yellow-100 text-yellow-800',
      revoked: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusClasses[status] || 'bg-gray-100 text-gray-800'
      }`}>
        {status}
      </span>
    );
  };

  const getTagBadge = (tag) => {
    const tagClasses = {
      'HSI': 'bg-blue-100 text-blue-800',
      'AHA': 'bg-purple-100 text-purple-800',
      'Renewed': 'bg-green-100 text-green-800',
      'Do Not Call': 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mr-1 mb-1 ${
        tagClasses[tag] || 'bg-gray-100 text-gray-800'
      }`}>
        {tag}
      </span>
    );
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryIndicator = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days === null) return null;
    
    if (days < 0) {
      return <span className="text-red-600 font-medium">Expired {Math.abs(days)} days ago</span>;
    } else if (days <= 30) {
      return <span className="text-yellow-600 font-medium">Expires in {days} days</span>;
    } else {
      return <span className="text-green-600">Expires in {days} days</span>;
    }
  };

  return (
    <div className="page-content">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Certificates</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage all certificates in the system
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
            >
              <FiPlus className="-ml-1 mr-2 h-4 w-4" />
              Add Certificate
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-5">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Certificate Management Hub
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Add, renew, and track client certificates. Monitor expiry dates and manage certificate details efficiently.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search certificates..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Certificate Type Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.certificate_type}
              onChange={(e) => handleFilterChange('certificate_type', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="AHA">AHA</option>
              <option value="HSI">HSI</option>
              <option value="Red Cross">Red Cross</option>
              <option value="Custom">Custom</option>
            </select>

            {/* Status Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>

            {/* Expiry Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.expiry_days}
              onChange={(e) => handleFilterChange('expiry_days', e.target.value)}
            >
              <option value="">All Expiry</option>
              <option value="7">Expiring in 7 days</option>
              <option value="30">Expiring in 30 days</option>
              <option value="60">Expiring in 60 days</option>
              <option value="90">Expiring in 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Certificates Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Certificates ({pagination.totalCertificates})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Certificate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiry Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!Array.isArray(certificates) || certificates.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No certificates found
                    </td>
                  </tr>
                ) : (
                  certificates.map((cert, index) => (
                    <tr key={cert.id || index} className="stagger-item hover:bg-gray-50 transition-all duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {cert.certificate_type || cert.type || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {cert.certificate_id || cert.id || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {cert.user_name || cert.user?.first_name + ' ' + cert.user?.last_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {cert.user_email || cert.user?.email || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(cert.status || 'unknown')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(cert.expiry_date)}
                        </div>
                        <div className="text-sm">
                          {getExpiryIndicator(cert.expiry_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap">
                          {Array.isArray(cert.tags) && cert.tags.length > 0 ? (
                            cert.tags.map((tag, tagIndex) => (
                              <span key={tagIndex}>{getTagBadge(typeof tag === 'string' ? tag : tag.name || 'Unknown')}</span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No tags</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!isLimitedAdmin() && (
                          <button className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200" onClick={() => handleEditCertificate(cert)}>
                            <FiEdit className="mr-1 h-4 w-4" />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <CreateCertificateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCertificateCreated={handleCertificateCreated}
      />

      {/* Edit Certificate Modal */}
      {
        showEditModal && (
          <EditCertificateModal
            data={selectedCertificate}
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onCertificateUpdated={handleCertificateUpdated}
          />
        )
      }
    </div>
  );
};

export default Certificates;